/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9: My training › Records. One current record per movement,
 * grouped like the fitness profile; a row opens the movement page.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Skeleton } from "@lemonadetraining/web-ui";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import type { MovementRegistryEntry } from "@lemonadetraining/contracts";
import { recordsByAxis } from "../../lib/recordsByAxis";
import { combineStatus, currentRecordsFromState } from "../../lib/movementStateRecords";
import { formatResultDay } from "../../lib/resultDates";
import type { UseMovementStateResult } from "../../hooks/useMovementState";
import { openMovementSearch } from "../movement/movementSearchEvents";

export interface RecordsTabProps {
  movementState: Pick<UseMovementStateResult, "status" | "movements">;
  registry: { status: "loading" | "ready" | "error"; movements: MovementRegistryEntry[] };
}

export default function RecordsTab({ movementState, registry }: RecordsTabProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const records = useMemo(
    () => currentRecordsFromState(movementState.movements, registry.movements),
    [movementState.movements, registry.movements]
  );
  const groups = useMemo(() => recordsByAxis(records, query), [records, query]);
  const hasAny = records.length > 0;
  // Results exist but none maps to a movement this page can show.
  const allHidden = !hasAny && movementState.movements.size > 0;

  const status = combineStatus(registry.status, movementState.status);
  const loading = status === "loading";
  const failed = status === "error";

  const logButton = (
    <Button onClick={() => openMovementSearch({ log: true })} title="Log a result (⌘L)">
      <IconPlus aria-hidden="true" /> Log a result
    </Button>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="training-records">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your records"
          aria-label="Search your records"
          className="h-8 w-full sm:w-60"
          data-testid="records-search"
        />
        <div className="ml-auto">{logButton}</div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3" data-testid="records-loading">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : failed ? (
        <div
          className="flex flex-col items-start gap-2 rounded-xl border p-4"
          role="alert"
          data-testid="records-error"
        >
          <p className="text-sm font-medium">We couldn&apos;t load your records</p>
          <p className="text-sm text-muted-foreground">
            Check your connection and try again. Your results are safe.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      ) : allHidden ? (
        <div className="rounded-xl border p-4" role="status" data-testid="records-hidden">
          <p className="text-sm text-muted-foreground">
            Some of your results can&apos;t be shown right now. Nothing you logged is lost.
          </p>
        </div>
      ) : !hasAny ? (
        <div
          className="flex flex-col items-start gap-2 rounded-xl border p-4"
          data-testid="records-empty"
        >
          <p className="text-sm font-medium">No records yet</p>
          <p className="text-sm text-muted-foreground">
            Your current record for each movement shows here, grouped by the five areas of your
            fitness profile.
          </p>
          {logButton}
        </div>
      ) : groups.length === 0 ? (
        <div
          className="flex flex-col items-start gap-2 rounded-xl border p-4"
          role="status"
          data-testid="records-no-match"
        >
          <p className="text-sm font-medium">No record matches “{query.trim()}”</p>
          <p className="text-sm text-muted-foreground">
            Check the spelling, or search all movements to log a first result.
          </p>
          <Button variant="outline" onClick={() => openMovementSearch({ log: true })}>
            Search movements
          </Button>
        </div>
      ) : (
        groups.map((group) => (
          <section
            key={group.label}
            aria-labelledby={`records-${group.axis ?? "other"}`}
            className="flex flex-col gap-2"
            data-testid={`records-group-${group.axis ?? "other"}`}
          >
            <h2 id={`records-${group.axis ?? "other"}`} className="text-sm font-medium">
              {group.label}{" "}
              <span className="font-normal text-muted-foreground">{group.rows.length}</span>
            </h2>
            <ul className="divide-y rounded-xl border bg-card">
              {group.rows.map((row) => (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/movements/${row.movementID}`)}
                    className="flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    data-testid={`records-row-${row.movementID}`}
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                    <span className="flex shrink-0 flex-col items-end">
                      <span className="font-semibold tabular-nums">{row.value}</span>
                      <span className="text-xs text-muted-foreground">
                        {row.statLabel}
                        {row.performedAt ? ` · ${formatResultDay(row.performedAt)}` : ""}
                      </span>
                    </span>
                    <IconChevronRight
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {!loading && !failed && hasAny && (
        <p className="text-xs text-muted-foreground">
          One current record per movement, grouped like your fitness profile.
        </p>
      )}
    </div>
  );
}
